/**
 * Recursive conditions evaluator for Form Builder V2.
 * Supports nested AND/OR logic (max depth 5) to control field visibility based on
 * form configuration and feature flags. Fails closed (hides field) on invalid configs.
 */

import get from 'lodash/get';

import type { FormData } from '@components/common/formSchema/types';

interface ConfigurationOperand {
  type: 'configuration';
  key: string;
  value: boolean | string | number;
}

interface FeatureFlagOperand {
  type: 'featureFlag';
  key: string;
  value: boolean | string | number;
}

type OperandNode = ConfigurationOperand | FeatureFlagOperand;

/**
 * Composite node - represents a logical combination of conditions.
 * Can contain other CompositeNodes (nested branches) or OperandNodes (leaves).
 *
 * @example
 * // (tier === "enterprise" AND region === "US") OR betaAccess === true
 * {
 *   operator: 'AND',
 *   operands: [
 *     {
 *       operator: 'AND',
 *       operands: [
 *         { type: 'configuration', key: 'tier', value: 'enterprise' },
 *         { type: 'configuration', key: 'region', value: 'US' }
 *       ]
 *     },
 *     { type: 'featureFlag', key: 'betaAccess', value: true }
 *   ]
 * }
 */
interface CompositeNode {
  operator: 'AND' | 'OR';
  operands: Array<OperandNode | CompositeNode>;
}

type Action = 'hide' | 'disable';

/**
 * @example
 * {
 *   expression: {
 *     operator: 'AND',
 *     operands: [
 *       { type: 'featureFlag', key: 'AMP_enable-braze-dest-multi-app-key-support', value: false },
 *       { type: 'configuration', key: 'usePlatformSpecificApiKeys', value: true },
 *       {
 *         operator: 'OR',
 *         operands: [
 *           { type: 'configuration', key: 'connectionMode.android', value: 'device' },
 *           { type: 'configuration', key: 'connectionMode.android', value: 'hybrid' }
 *         ]
 *       }
 *     ]
 *   },
 *   action: 'hide'
 * }
 */
export interface ConditionsConfig {
  expression: OperandNode | CompositeNode;
  action?: Action;
}

interface Resolvers {
  /**
   * Resolves a configuration value by key.
   * @param key - Configuration key (supports nested paths like "connectionMode.android")
   * @returns The value at the specified key (boolean, string, or number), or undefined if not found
   */
  configurationResolver: (key: string) => boolean | string | number | undefined;

  /**
   * Resolves a feature flag value by key.
   * @param key - Feature flag identifier
   * @returns The feature flag value (boolean, string, or number), or undefined if not found
   */
  featureFlagResolver: (key: string) => boolean | string | number | undefined;
}

/**
 * Maximum allowed nesting depth for condition expressions.
 * Rationale: Prevents overly complex logic that becomes unmaintainable.
 * If business needs require deeper nesting, this can be increased.
 */
const MAX_CONDITION_DEPTH = 5;

const isCompositeNode = (node: unknown): node is CompositeNode => {
  const obj = node as { operator?: unknown; operands?: unknown };
  return (
    (obj?.operator === 'AND' || obj?.operator === 'OR') &&
    Array.isArray(obj.operands) &&
    obj.operands.length > 0
  );
};

const isConfigurationOperand = (node: unknown): node is ConfigurationOperand => {
  const obj = node as { type?: string; key?: unknown; value?: unknown };
  return (
    obj?.type === 'configuration' &&
    typeof obj.key === 'string' &&
    obj.key.length > 0 &&
    obj.value !== undefined &&
    (typeof obj.value === 'boolean' ||
      typeof obj.value === 'string' ||
      typeof obj.value === 'number')
  );
};

const isFeatureFlagOperand = (node: unknown): node is FeatureFlagOperand => {
  const obj = node as { type?: string; key?: unknown; value?: unknown };
  return (
    obj?.type === 'featureFlag' &&
    typeof obj.key === 'string' &&
    obj.key.length > 0 &&
    obj.value !== undefined &&
    (typeof obj.value === 'boolean' ||
      typeof obj.value === 'string' ||
      typeof obj.value === 'number')
  );
};

const evaluateOperand = (node: OperandNode, resolvers: Resolvers): boolean => {
  const actualValue =
    node.type === 'configuration'
      ? resolvers.configurationResolver(node.key)
      : resolvers.featureFlagResolver(node.key);

  return actualValue === node.value;
};

const evaluateComposite = (
  composite: CompositeNode,
  resolvers: Resolvers,
  currentDepth = 0,
): boolean => {
  if (composite.operator === 'AND') {
    return composite.operands.every((operand) =>
      evaluateExpression(operand, resolvers, currentDepth + 1),
    );
  }

  return composite.operands.some((operand) =>
    evaluateExpression(operand, resolvers, currentDepth + 1),
  );
};

const evaluateExpression = (
  node: OperandNode | CompositeNode,
  resolvers: Resolvers,
  currentDepth = 0,
): boolean => {
  if (isCompositeNode(node)) {
    return evaluateComposite(node, resolvers, currentDepth);
  }

  return evaluateOperand(node, resolvers);
};

/**
 * Validates expression structure and enforces max depth limit.
 * @throws Error if structure is invalid or exceeds max depth
 */
function parseExpression(
  json: unknown,
  maxDepth: number = MAX_CONDITION_DEPTH,
  currentDepth = 0,
): OperandNode | CompositeNode {
  if (!json) {
    throw new Error('[Conditions Validator] Expression cannot be null or undefined');
  }

  if (isCompositeNode(json)) {
    if (currentDepth >= maxDepth) {
      throw new Error(
        `[Conditions Validator] Maximum nesting depth (${maxDepth}) exceeded at depth ${currentDepth}`,
      );
    }

    json.operands.forEach((operand, index) => {
      try {
        parseExpression(operand, maxDepth, currentDepth + 1);
      } catch (error) {
        throw new Error(
          `[Conditions Validator] Error in operand ${index} of ${json.operator}: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      }
    });

    return json;
  }

  if (isConfigurationOperand(json) || isFeatureFlagOperand(json)) {
    return json;
  }

  throw new Error(
    '[Conditions Validator] Invalid operand node. Must have valid type ("configuration" or "featureFlag"), non-empty string key, and boolean/string value.',
  );
}

interface CheckConditionsContext {
  formData: FormData;
  availableFeatureFlags: Record<string, boolean>;
  fieldDefaults?: Record<string, unknown>;
}

/**
 * Evaluates conditions and returns visibility/enablement state.
 *
 * @param conditionsConfig - Condition tree from ui-config.json
 * @param context - Form data, feature flags, and field defaults
 * @returns shouldShow (visible) and shouldDisable (read-only) flags
 *
 * Action modes:
 * - "hide" (default): False expression hides field
 * - "disable": False expression shows but disables field
 *
 * @example
 * const { shouldShow, shouldDisable } = checkConditions(field.conditions, {
 *   formData,
 *   availableFeatureFlags,
 *   fieldDefaults
 * });
 */
export const checkConditions = (
  conditionsConfig: ConditionsConfig | null | undefined,
  context: CheckConditionsContext,
): { shouldShow: boolean; shouldDisable: boolean } => {
  if (!conditionsConfig || !conditionsConfig.expression) {
    return { shouldShow: true, shouldDisable: false };
  }

  let parsedNode: OperandNode | CompositeNode;
  try {
    parsedNode = parseExpression(conditionsConfig.expression);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Conditions Evaluator] Validation failed:', error);
    return { shouldShow: false, shouldDisable: false };
  }

  const resolvers: Resolvers = {
    configurationResolver: (key: string): boolean | string | number | undefined => {
      const actualValue = get(context.formData, key);

      if (actualValue === undefined || actualValue === null) {
        const defaultValue = context.fieldDefaults?.[key];
        return defaultValue as boolean | string | number | undefined;
      }

      return actualValue as boolean | string | number;
    },

    featureFlagResolver: (key: string): boolean | string | number | undefined => {
      const actualValue = get(context.availableFeatureFlags, key);

      if (actualValue === undefined || actualValue === null) {
        return false;
      }

      return actualValue as boolean | string | number;
    },
  };

  const result = evaluateExpression(parsedNode, resolvers);
  const action = conditionsConfig.action || 'hide';

  let shouldShow: boolean;
  let shouldDisable: boolean;

  if (result) {
    shouldShow = true;
    shouldDisable = false;
  } else if (action === 'disable') {
    shouldShow = true;
    shouldDisable = true;
  } else {
    shouldShow = false;
    shouldDisable = false;
  }

  return { shouldShow, shouldDisable };
};
